import { FormEvent, useContext, useState } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeCardElementChangeEvent } from '@stripe/stripe-js'
import { useMutation } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useAtom } from 'jotai'
import Cookies from 'js-cookie'
import _ from 'lodash'

import { cartService } from '@/domain/Cart'
import { CompleteCartDto } from '@/domain/Cart/dtos/complete-cart.dto'
import { CartContext } from '@/modules/Cart/Cart.vm'
import { localCartAtom } from '@/shared/states/cart'
import { MinimalCart } from '@/shared/types/cart'

import { CardFormProps } from './CardForm'
import { cardFormMachine } from './CardForm.machine'

export const useViewModel = (p: CardFormProps) => {
    const [hasFilledCard, setHasFilledCard] = useState(false)
    const [showLoadingOverlay, setLoadingOverlay] = useState(false)

    const [, setCart] = useAtom(localCartAtom)

    const {
        cartActor: [actor, cartActorSend],
    } = useContext(CartContext)

    const elements = useElements()
    const stripe = useStripe()

    const completeCart = useMutation({
        mutationFn: async ({ cart_id }: CompleteCartDto) =>
            await cartService.complete({ cart_id }),
    })

    const [state, send] = useMachine(cardFormMachine)

    /**
     * ? When the cart is completed, we remove the cart id from the cookies, reset the cart atom.
     */
    const handleCartComplete = () => {
        Cookies.remove('cart_id')

        const minimalCart: MinimalCart = {
            id: _.uniqueId('cart'),
            items: [],
            total: 0,
        }

        setCart(minimalCart)
    }

    const onPaymentElementChange = (e: { complete: boolean }) => {
        setHasFilledCard(e.complete)
    }

    const onStartPayment = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setLoadingOverlay(true)
        if (!elements || !stripe || !hasFilledCard) {
            return
        }

        try {
            if (!actor.context.cart?.id) {
                throw new Error('Cart id not found')
            }

            if (!stripe) {
                throw new Error('Stripe not found')
            }

            const paymentIntentExist = await stripe.retrievePaymentIntent(
                p.client_secret,
            )

            sessionStorage.setItem('pending_cart_id', actor.context.cart.id)

            if (
                paymentIntentExist.paymentIntent?.status !== 'requires_capture'
            ) {
                const { error } = await stripe.confirmPayment({
                    elements,
                    confirmParams: {
                        return_url: `${window.location.origin}/orders/confirm`,
                        payment_method_data: {
                            billing_details: {
                                ...p.billing_address,
                            },
                        },
                    },
                })
                // On arrive ici uniquement si erreur (pas de redirection)
                if (error) {
                    console.log('Erreur paiement:', error.message)
                    send({ type: 'THROW' })
                }
            }
        } catch (err) {
            console.log(err)
            send({ type: 'THROW' })
        }
    }

    return {
        onStartPayment,
        totalAmount: actor.context.cart?.total || 0,
        state,
        hasFilledCard,
        onPaymentElementChange,
        showLoadingOverlay,
        setLoadingOverlay,
    }
}
